import { includes } from 'libraries/lodash'
import { RESPONSE_PENDING } from 'front-end-core/constants/responses'
import getUrl from 'front-end-core/core/url'

import { CURRENT_AWARENESS, LEGISLATION, QA_CONTENT } from 'constants/controllers'
import { searchResultExpandCallback as caExpandCallback } from 'services/CurrentAwarenessService'
import { expandCallback as qaExpandCallback } from 'services/QAService'
import { expandCallback as legExpandCallback } from 'services/LegislationService'
import {
  SET_ACCORDION_EXPANDED_NODE_ID,
  SET_ACCORDION_EXPANDED_NODE_TITLE,
  SET_ACCORDION_HIGHLIGHT,
  SET_ACCORDION_GROUP,
  SET_ACCORDION_EVENT_CATEGORY,
  SET_ACCORDION_HAS_EXPAND_CALLBACK,
  SET_ACCORDION_BREADCRUMB_CLICKED,
  SET_ACCORDION_CAN_ACCESS,
  SET_ACCORDION_SECTION_ID,
  SET_ACCORDION_URL_NODE,
  SET_ACCORDION_COMMENT_CONTROLS_ACTIVE_ID
} from './types'
import { AccordionState } from 'redux/reducers/accordion'
import { AccordionGroup, ExpandCallback } from 'interfaces/accordion';
import { AppDispatch, RootState } from 'redux/store'

const tocIdMap = new Map() // tocId -> <groupId>

const updateTocIdMapFromGroup = (group: AccordionGroup[]) => {
  for (let i = 0; i < group.length; i++) {
    for (let j = 0; j < group[i].tocIds.length; j++) {
      if (!tocIdMap.has(group[i].tocIds[j])) {
        tocIdMap.set(group[i].tocIds[j], group[i].id)
      }
    }
  }
}

export const setAccordionExpandedNodeId = (expandedNodeId: string) => ({
  type: SET_ACCORDION_EXPANDED_NODE_ID,
  expandedNodeId
})

export const setAccordionExpandedNodeTitle = (expandedNodeTitle: string) => ({
  type: SET_ACCORDION_EXPANDED_NODE_TITLE,
  expandedNodeTitle
})

export const setAccordionHighlight = (highlight: string) => ({
  type: SET_ACCORDION_HIGHLIGHT,
  highlight
})

export const setAccordionGroup = (group: AccordionGroup[]) => ({
  type: SET_ACCORDION_GROUP,
  group
})

export const setAccordionHasExpandCallback = (hasExpandCallback: boolean) => ({
  type: SET_ACCORDION_HAS_EXPAND_CALLBACK,
  hasExpandCallback
})

export const setAccordionEventCategory = (eventCategory: string) => ({
  type: SET_ACCORDION_EVENT_CATEGORY,
  eventCategory
})

export const setAccordionBreadCrumbClicked = (breadCrumbClicked: boolean) => ({
  type: SET_ACCORDION_BREADCRUMB_CLICKED,
  breadCrumbClicked
})

export const setAccordionCanAccess = (canAccess: boolean) => ({
  type: SET_ACCORDION_CAN_ACCESS,
  canAccess
})

export const setAccordionSectionId = (sectionId: string) => ({
  type: SET_ACCORDION_SECTION_ID,
  sectionId
})

export const setAccordionUrlNode = (urlNode: string) => ({
  type: SET_ACCORDION_URL_NODE,
  urlNode
})

export const setAccordionState = ({ highlight, eventCategory, group, expandCallback, sectionId }: Partial<AccordionState> & ExpandCallback) => {
  return (dispatch: AppDispatch, getState: () => RootState) => {
    dispatch(setAccordionEventCategory(eventCategory))
    dispatch(setAccordionHasExpandCallback(!!expandCallback))
    dispatch(setAccordionHighlight(highlight))
    dispatch(setAccordionGroup(group))
    dispatch(setAccordionSectionId(sectionId))

    const {
      layout: { flavour, controller, pageData: { fromDate, toDate } },
      accordion: { expandedNodeId, breadCrumbClicked },
      user: { isLimitedAccess },
      aol: { name: aol }
    } = getState()

    let canAccess = true

    const highlightPathItems = highlight && highlight.split('/')
    const highlightId = highlightPathItems && highlightPathItems[highlightPathItems.length - 1]

    let url
    switch (controller) {
      case CURRENT_AWARENESS:
        url = getUrl(CURRENT_AWARENESS, { fromDate, toDate })
        break
      case LEGISLATION:
        url = getUrl(LEGISLATION, { aol, sectionId, focusedId: highlightId })
        break
      case QA_CONTENT:
        url = getUrl(QA_CONTENT, { aol, flavour, sectionId, focusedId: highlightId })
        break
      default:
        console.error('unknown controller in setAccordionState:', controller)
        break
    }

    dispatch(setAccordionUrlNode(url))

    if (eventCategory === 'Q&A' && group !== RESPONSE_PENDING && !breadCrumbClicked) {
      const groupFound = group.find((item: {id:string, sectionIds: string[]}) =>
        // Solution to FC-1484, Links to S1 sections should expand to correct path
        ('#'.concat(item.id) === highlight) ||
        // Solution to FC-1542, Links to S2 sections should expand to correct path
        (item.sectionIds.some((sectionId: string) => sectionId === highlightId))
      )

      if (groupFound) {
        const path = groupFound.path.join('/')
        if (expandCallback(path, 'group', flavour, 1)) {
          if (expandedNodeId !== groupFound.id) {
            dispatch(setAccordionExpandedNodeId(groupFound.id))
          }
        }
      } else if (highlight) {
        // Solution to FC-1031, clicking back after clicking through question links

        if (!tocIdMap.has(highlightId)) {
          updateTocIdMapFromGroup(group)
        }

        const groupId = tocIdMap.get(highlightId)
        if (groupId && groupId !== expandedNodeId) {
          dispatch(setAccordionExpandedNodeId(groupId))
        } else {
          // TODO: Investigate if this is best way to check for premium S2 links
          const isS2 = highlightPathItems.length > 4
          // Partial solution to FC-1528: Show role modal to core users when S2 url is premium
          if (isS2 && !groupId && !expandedNodeId && isLimitedAccess) {
            canAccess = false
          }
        }
      }
    }

    dispatch(setAccordionCanAccess(canAccess))
  }
}

export const updateExpandedNodeIdState = (id:string, value: string) => {
  return (dispatch: AppDispatch, getState: () => RootState) => {
    const { group } = getState().accordion
    const idList = group && group.map((node: { id: any }) => node.id)

    if (+value) {
      if (includes(idList, id)) {
        dispatch(setAccordionExpandedNodeId(id))
      }
    } else {
      if (includes(idList, id)) {
        dispatch(setAccordionExpandedNodeId(null))
        dispatch(setAccordionExpandedNodeTitle(null))
      }
    }
  }
}

export const handleExpandedTitleOnClick = () => {
  return (dispatch: AppDispatch, getState: () => RootState) => {
    const { expandedNodeId, hasExpandCallback, eventCategory, group } = getState().accordion
    const path = group.find((node: { id: string }) => node.id === expandedNodeId).path.join('/')

    if (hasExpandCallback) {
      switch (eventCategory) {
        case 'CA':
          caExpandCallback('expand', 'group', path, 'false')
          break
        case 'Legislation':
          legExpandCallback('expand', 'group', path, 'false')
          break
        case 'Q&A':
          qaExpandCallback('expand', 'group', path, 'false')
          break
        default:
          console.error('eventCategory not recognised')
          break
      }
    }
    dispatch(setAccordionBreadCrumbClicked(true))
    dispatch(setAccordionExpandedNodeId(null))
    dispatch(setAccordionExpandedNodeTitle(null))
  }
}

export const setAccordionCommentControlsActiveId = (activeId: string) => ({
  type: SET_ACCORDION_COMMENT_CONTROLS_ACTIVE_ID,
  activeId
})
